
import {Component, Mixins, Prop, Vue, Watch} from "vue-property-decorator";
import PickerInputMixin from "@/mixins/PickerInputMixin.vue";
import MahkemeForm from "@/components/forms/MahkemeForm.vue";
import {MahkemeEntity} from "@/entity/MahkemeEntity";
import {mahkemeIliskileriYuklu} from "@/utils/mahkemeSecim";

@Component({
  components: {MahkemeForm},
})
export default class MahkemeSecim extends Mixins(PickerInputMixin) {
  @Prop() hideDetails!: string | boolean;
  @Prop({default: null}) item!: MahkemeEntity | null;
  @Prop({type: Object}) defaultValues!: any;
  @Prop({required: false}) alacakBilgisi?: any;

  readonly emptyAlacakBilgisiStub = { mahkeme_id: null as number | null, ozel_mahkeme_isim: "" };

  dialog = false;
  items!: Array<MahkemeEntity>;
  /**
   * DEV-2261: her açılışta artar ve MahkemeForm'u yeniden kurar. Form önceki açılışın
   * durumunu (Vazgeç ile bırakılan değişiklikler dahil) taşımaz; alanlar her seferinde o anki
   * seçimden doldurulur. Önceden form ilk açılışta bir kez kuruluyordu; alacak düzenleme
   * dialogu da her açılışta yeniden kurulduğu için modal kayıtlı mahkemeyi hiç görmüyordu.
   */
  formAnahtari = 0;
  /** Modal açılırken forma verilen, hâlihazırda seçili mahkeme (ilişkileriyle). */
  modalMahkemesi: MahkemeEntity | null = null;
  modalHazirlaniyor = false;

  get isHideDetails() {
    return this.hideDetails || this.hideDetails === "";
  }

  /**
   * Aynı tıklamada hem + ikonu hem alanın kendisi (click.native) çağırır; ikinci çağrı yok sayılır.
   */
  modaliAc() {
    if (this.dialog || this.modalHazirlaniyor)
      return;
    const mahkeme = this.iliskiliSeciliMahkeme();
    const eksikId = mahkeme ? null : this.resolveMahkemeIdAndEntity().id;
    if (eksikId == null) {
      this.modaliGoster(mahkeme);
      return;
    }
    // Seçim var ama ilişkili kaydı elde yok: mahkeme listesi henüz yüklenmedi ya da kayıt listede değil.
    this.modalHazirlaniyor = true;
    this.$http.get(`/api/v1/mahkeme/${eksikId}`)
        .then((response: any) => this.modaliGoster(mahkemeIliskileriYuklu(response?.data) ? response.data : null))
        .catch(() => this.modaliGoster(null))
        .finally(() => {
          this.modalHazirlaniyor = false;
        });
  }

  private modaliGoster(mahkeme: MahkemeEntity | null) {
    this.modalMahkemesi = mahkeme;
    this.formAnahtari++;
    this.dialog = true;
  }

  private iliskiliSeciliMahkeme(): MahkemeEntity | null {
    const secili: any = this.selectedItem;
    if (mahkemeIliskileriYuklu(secili))
      return secili;
    const {entity} = this.resolveMahkemeIdAndEntity();
    return mahkemeIliskileriYuklu(entity) ? entity : null;
  }

  /**
   * v-model id veya tam mahkeme nesnesi; store listesinde yoksa (PickerInputMixin update silmesin diye) listeye eklenir.
   */
  private resolveMahkemeIdAndEntity(): { id: number | null; entity: MahkemeEntity | null } {
    const fromProp = this.item || this.alacakBilgisi?.mahkeme;
    if (this.value && typeof this.value === "object" && "id" in this.value) {
      const entity = this.value as MahkemeEntity;
      const id = Number(entity.id);
      return { id: Number.isFinite(id) ? id : null, entity };
    }
    const rawId = this.alacakBilgisi?.mahkeme_id ?? this.value;
    if (rawId == null || rawId === "") {
      return { id: null, entity: null };
    }
    const id = Number(rawId);
    if (!Number.isFinite(id)) {
      return { id: null, entity: null };
    }
    const entity =
      fromProp && Number(fromProp.id) === id ? (fromProp as MahkemeEntity) : null;
    return { id, entity };
  }

  private mergeMahkemeIntoStoreListIfMissing(list: Array<MahkemeEntity>): void {
    const { id, entity } = this.resolveMahkemeIdAndEntity();
    if (id == null || entity == null || Number(entity.id) !== id) {
      return;
    }
    if (!list.some((m) => m.id === id)) {
      list.push(entity);
    }
  }

  async mounted() {
    try {
      await this.$store.dispatch("getMahkemeler");
    } catch (e) {
      console.error("Mahkemeler yüklenemedi:", e);
    }
    const list = this.$store.state.mahkemeler as Array<MahkemeEntity>;
    this.mergeMahkemeIntoStoreListIfMissing(list);
    this.$store.commit("resortMahkemeler");
    this.items = list;
    await this.$nextTick();
    (this as Vue & { update: () => void }).update();
  }

  /*async load() {
    await this.$http.get('/api/v1/mahkeme/').then(response => {
      this.items = response.data;
    })
  }*/

  /**
   * PickerInputMixin.update() seçimi programatik olarak kurduğunda v-select'in input olayı
   * tetiklenmiyor; bu yüzden düzenleme modunda mahkemeData hiç yayınlanmıyor ve tüketiciler
   * (dosya türü filtresi, otomatik alacak açıklaması) mahkeme bilgisine ulaşamıyordu.
   */
  @Watch('selectedItem', {immediate: true})
  onSelectedItemResolved(item: MahkemeEntity | undefined) {
    if (item)
      this.$emit('mahkemeData', item);
  }

  onSelectedItemChanged() {
    this.selectedId = this.value;
    this.$emit('mahkemeData', this.selectedItem);
    this.input();
  }

  /**
   * Modalın dışarıya tek çıkışı (Kaydet). Formun alan değişiklikleri dinlenmez: eskiden
   * <mahkeme-form @input> her değişiklikte seçimi yeniden yayınlıyordu; mahkeme listesi henüz
   * yüklenmemişken bu boş seçim olarak gidip kayıtlı mahkemeyi siliyordu (DEV-2261).
   */
  async saveMahkeme(eklenenMahkeme: any) {
    this.dialog = false;

    const yeniMahkeme = eklenenMahkeme.mahkeme;
    // items mahkeme listesinin (store) kendisi ve seçilen mahkeme orada zaten var; koşulsuz
    // push her düzenlemede listeye aynı mahkemeden bir kopya daha ekliyordu (DEV-2261).
    if (!this.items.some((m) => m.id === yeniMahkeme.id))
      this.items.push(yeniMahkeme);
    this.$store.commit('resortMahkemeler');
    this.selectedItem = yeniMahkeme;

    this.$emit('mahkemeData', yeniMahkeme);
    this.input();
  }
}
